import type { BoxProps } from '@chakra-ui/react';
import { chakra, HStack, Text, VStack } from '@chakra-ui/react';
import { Button, WrappedImage } from '@time-line/components/common';
import { FormatPrice } from 'components/localisation/FormatPrice';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import NextLink from 'next/link';

import { ProductAddToCartButton } from '../ProductAddToCartButton';
import type { ProductComplementaryProps } from '.';

type BaseProductProps = {
  slug: string;
  href: string;
  image: ProductComplementaryProps['products'][0]['image'];
  variant: ProductComplementaryProps['products'][0]['variant'];
  ctaType?: ProductComplementaryProps['ctaType'];
  cardProps?: BoxProps;
};

type ComplementaryProductProps = BaseProductProps & {
  title?: never;
  subtitle?: never;
  label?: string;
};

type AlternativeProductProps = BaseProductProps & {
  title: string;
  subtitle: string;
  label?: string;
};

type ProductComplementaryProductProps =
  ComplementaryProductProps | AlternativeProductProps;

const isAlternativeProduct = (
  props: Omit<
    ProductComplementaryProductProps,
    'slug' | 'href' | 'image' | 'variant'
  >,
): props is AlternativeProductProps => 'title' in props && 'subtitle' in props;

export function ProductComplementaryProduct({
  slug,
  href,
  image,
  variant: selectedVariant,
  ctaType,
  cardProps = {},
  ...props
}: ProductComplementaryProductProps) {
  const { data: shopifyProduct } = useShopifyProduct(slug);

  if (!shopifyProduct) return null;

  const variant = shopifyProduct.variants.find(
    (v) => v.id === selectedVariant.gid,
  );

  if (!variant || !variant.availableForSale) return null;

  return (
    <HStack gap="sm" alignItems="space-between" w="full" {...cardProps}>
      <NextLink href={href}>
        <WrappedImage
          ratio={4 / 5}
          image={image}
          wrapperProps={{ width: '100px' }}
        />
      </NextLink>
      <VStack flex="1" justify="space-between" alignItems="flex-start">
        <VStack alignItems="flex-start" gap="3xs">
          <NextLink href={href}>
            {isAlternativeProduct(props) ? (
              <Text as="span" size="title2" width="200px" pr="3xs">
                {props.title}
                <br />
                <chakra.span color="blackAlpha.500">
                  {props.subtitle}
                </chakra.span>
              </Text>
            ) : (
              <Text size="title2">{shopifyProduct.title}</Text>
            )}
          </NextLink>

          <FormatPrice
            fontWeight="medium"
            fontFamily="sans"
            size="2xs"
            amount={variant.price.amount}
          />
        </VStack>
        {ctaType === 'addToCart' ? (
          <ProductAddToCartButton
            merchandiseId={variant.id}
            size="sm"
            variant="outline"
          />
        ) : (
          <Button as={NextLink} href={href} variant="outline" size="sm">
            {props.label}
          </Button>
        )}
      </VStack>
    </HStack>
  );
}
